import { useState } from 'react';
import { useMedia } from 'react-use';
import { Checklist, Never } from '@hungryroot/icons';
import { Paragraph, Heading } from '@hungryroot/ramen';
import {
  type ProductV1 as Product,
  useBundleModifyReasons,
  type Reason,
} from '@hungryroot/rest';
import {
  usePostSetPeriodicity,
  PeriodicityTypes,
} from '@hungryroot/rest-hooks';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import styles from './RemovalFeedback.module.css';

export type RemovalFeedbackProduct = Pick<
  Product,
  'card_image_url' | 'name' | 'id' | 'points' | 'amount'
>;

interface Props {
  testid?: string;
  product: RemovalFeedbackProduct;
  onIngredientWillNever: () => void;
  onReasonSelected: (id: number, reasonId: number) => void;
  isModalEditor?: boolean;
  pairingPoints: number;
  updateButton?: (
    onSelect: () => void,
    points: string,
    disabled?: boolean
  ) => JSX.Element;
  isCustomModalEditor?: boolean;
}

export function RemovalFeedback(props: Props) {
  const {
    testid,
    onReasonSelected,
    product,
    onIngredientWillNever,
    isModalEditor = false,
    pairingPoints,
    updateButton,
    isCustomModalEditor,
  } = props;
  const isDesktop = useMedia(BreakpointQueryLarge);
  const persistSetPeriodicty = usePostSetPeriodicity();
  const { card_image_url: img, name } = product;
  const { data } = useBundleModifyReasons();
  const [selectedReason, setSelectedReason] = useState<Reason | null>(null);

  const modifyReasons = data?.results
    .filter((reason) => reason.type === 2)
    .reverse();

  const headingText = `Why are you removing ${name}?`;

  const productPoints = product.points * product.amount;

  const handleModifyProduct = (reason: Reason | null, productId: number) => {
    if (reason === null) return;
    if (reason.id === 3) {
      onIngredientWillNever();
      persistSetPeriodicty.mutate({
        data: { product_id: productId, periodicity: PeriodicityTypes.NEVER },
      });
    }
    onReasonSelected(productId, reason.id);
  };

  const headerImg = () => {
    if (isModalEditor) {
      return (
        <div className={styles['header-modal-editor']}>
          <div className={styles['container-img-modal-editor']}>
            <img
              data-testid={`${testid}-img`}
              src={img || undefined}
              className={styles['img-modal-editor']}
              alt=""
            />
          </div>
          <Paragraph testid={`${testid}-heading`} variant="lead">
            {headingText}
          </Paragraph>
        </div>
      );
    }
    return (
      <div className={styles.header}>
        <div className={styles['container-img']}>
          <img
            data-testid={`${testid}-img`}
            src={img || undefined}
            className={styles.img}
            alt=""
          />
        </div>
        <Heading testid={`${testid}-heading`} variant={isDesktop ? 'H3' : 'H2'}>
          {headingText}
        </Heading>
      </div>
    );
  };

  const editorButtonsClass = isModalEditor ? styles.reason : '';

  return (
    <div data-testid={testid} className={styles.container}>
      {headerImg()}
      <div className={styles['button-container']}>
        {modifyReasons?.map((reason) => {
          const icon = reason.id === 3 ? <Never /> : <Checklist />;
          return (
            <button
              key={reason.id}
              type="submit"
              data-testid={`modify-reason-${reason.id}`}
              onClick={() => {
                isModalEditor && !isCustomModalEditor
                  ? setSelectedReason(reason)
                  : handleModifyProduct(reason, product.id);
              }}
              className={
                selectedReason && selectedReason === reason
                  ? styles.selected
                  : editorButtonsClass
              }
            >
              {icon}
              <Paragraph variant="body">
                {reason.id === 3
                  ? 'I never want this ingredient*'
                  : "I don't need this ingredient"}
              </Paragraph>
            </button>
          );
        })}
      </div>
      <Paragraph variant="caption" className={styles.caption}>
        *If you select 'I never want this ingredient,' we won't send it to you
        in the future.
      </Paragraph>
      {isModalEditor &&
        (updateButton
          ? updateButton(
              () => handleModifyProduct(selectedReason, product.id),
              (pairingPoints - productPoints).toString(),
              selectedReason === null
            )
          : null)}
    </div>
  );
}
